import React from 'react'
import { Alert, AutoComplete, Button, Card, Col, Form, Row, Skeleton, Switch, Tag, Typography } from 'antd'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { PushlyAi } from '@pushly/aqe/lib/components'
import { DomainDto } from '../../../../../dtos/domain'
import { canShowField } from '../helpers'
import {
    PUSHLY_AI_NUM_VARIATIONS,
    PushlyAiGenerationFlow,
    PushlyAiGenerationOptions,
    PushlyAiVariantSlot,
    TONE_PRESETS,
    normalizeTone,
    toneError,
    usesBrandVoice,
} from '../../pushly-ai-generation/generation-options'
import { RemoteErrorMessageDisplay } from './remote-error-message-display'

/**
 * Skeletons where a result's content will be — the reason, then each field the domain lets Pushly
 * AI fill — so the wait looks like what arrives.
 */
const VariantBodySkeleton = ({ domain, active }: { domain: DomainDto; active: boolean }) => (
    <div className="variant-skeleton">
        <div className="field-section reason-section block-quote">
            <div className="reason-label">Why this Variant?</div>
            <div className="reason-text">
                <Skeleton active={active} title={false} paragraph={{ rows: 2, width: ['100%', '60%'] }} />
            </div>
        </div>
        {canShowField(domain, 'title', 'ai_recommendations') && (
            <div className="field-section">
                <div className="skeleton-field-label">Title</div>
                <Skeleton active={active} title={false} paragraph={{ rows: 1, width: '85%' }} />
            </div>
        )}
        {canShowField(domain, 'body', 'ai_recommendations') && (
            <div className="field-section">
                <div className="skeleton-field-label">Body</div>
                <Skeleton active={active} title={false} paragraph={{ rows: 2, width: ['100%', '70%'] }} />
            </div>
        )}
        {canShowField(domain, 'imageUrl', 'ai_recommendations') && (
            <div className="field-section">
                <div className="skeleton-field-label">Image</div>
                <Skeleton.Node active={active} className="skeleton-image">
                    <span />
                </Skeleton.Node>
            </div>
        )}
    </div>
)

interface IVariantOptions {
    variant: number
    slot: PushlyAiVariantSlot
    hasBrandVoice: boolean
    onChange(slot: PushlyAiVariantSlot): void
}

/** One variant's settings, stacked as labelled rows. A new per-variant option is a new row here. */
const VariantOptions = ({ variant, slot, hasBrandVoice, onChange }: IVariantOptions) => {
    const error = toneError(slot.tone)

    return (
        <Form component="div" layout="horizontal" colon={false} labelAlign="left" className="variant-options">
            <Form.Item label="Brand Voice" extra={hasBrandVoice ? undefined : 'Set up a Brand Voice to use it.'}>
                <Switch
                    size="small"
                    aria-label={`Variant ${variant} Brand Voice`}
                    checked={usesBrandVoice(slot, hasBrandVoice)}
                    disabled={!hasBrandVoice}
                    onChange={(checked) => onChange({ ...slot, brandVoice: checked })}
                />
            </Form.Item>
            <Form.Item
                label="Tone"
                tooltip={
                    hasBrandVoice
                        ? 'Pick a tone or type your own. Auto spreads tones across variants, keeping ones your Brand Voice discourages off its variants.'
                        : 'Pick a tone or type your own. Auto spreads tones across variants.'
                }
                validateStatus={error ? 'error' : undefined}
                help={error ?? undefined}
            >
                <AutoComplete
                    aria-label={`Variant ${variant} tone`}
                    className="variant-tone"
                    value={slot.tone ?? ''}
                    options={TONE_PRESETS}
                    placeholder="Auto, or type your own"
                    allowClear
                    filterOption={(input, option) =>
                        String(option?.label ?? '')
                            .toLowerCase()
                            .includes(input.trim().toLowerCase())
                    }
                    onChange={(value: string) => onChange({ ...slot, tone: value || null })}
                />
            </Form.Item>
        </Form>
    )
}

interface IDraftVariantCard {
    variant: number
    slot: PushlyAiVariantSlot
    domain: DomainDto
    hasBrandVoice: boolean
    generating: boolean
    canRemove: boolean
    onChange(slot: PushlyAiVariantSlot): void
    onRemove(): void
}

/**
 * A variant before it is generated, in the same card and inner well its result will fill. While
 * setting up, the well holds the variant's settings, with the rest left open for options to come;
 * an invisible skeleton of the result underneath keeps the card at its result's size, so nothing
 * moves when Generate shows it. While generating, the settings give way to that skeleton, animated,
 * and the header keeps what was asked for.
 */
const DraftVariantCard = ({
    variant,
    slot,
    domain,
    hasBrandVoice,
    generating,
    canRemove,
    onChange,
    onRemove,
}: IDraftVariantCard) => (
    <Card
        className="variant-card-2 draft-variant-card"
        size="small"
        style={{ height: '100%' }}
        data-variant={variant}
        title={
            <div className="variant-card-title">
                <div className="variant-card-title-controls">
                    <Tag className="suggestion-chip">{variant}</Tag>
                    {generating && (
                        <span className="draft-variant-tone">{normalizeTone(slot.tone) ?? 'Auto tone'}</span>
                    )}
                    {generating && usesBrandVoice(slot, hasBrandVoice) && (
                        <Tag className="brand-voice-tag">Brand Voice</Tag>
                    )}
                </div>
                {!generating && (
                    <Button
                        size="small"
                        type="text"
                        aria-label={`Remove variant ${variant}`}
                        icon={<DeleteOutlined />}
                        disabled={!canRemove}
                        onClick={onRemove}
                    />
                )}
            </div>
        }
    >
        <Card className="variant-card-2-content" type="inner" size="small">
            <div className="draft-variant-body">
                <div className="draft-variant-result" aria-hidden={!generating || undefined}>
                    <VariantBodySkeleton domain={domain} active={generating} />
                </div>
                {!generating && (
                    <VariantOptions variant={variant} slot={slot} hasBrandVoice={hasBrandVoice} onChange={onChange} />
                )}
            </div>
        </Card>
    </Card>
)

/** A card the generation was asked for but didn't return, so the gap is explained, not hidden. */
export const MissingVariantCard = ({ variant }: { variant: number }) => (
    <Card
        className="variant-card-2 missing-variant-card"
        size="small"
        style={{ height: '100%' }}
        title={
            <div className="variant-card-title">
                <div className="variant-card-title-controls">
                    <Tag className="suggestion-chip">{variant}</Tag>
                    Not generated
                </div>
            </div>
        }
    >
        <Card className="variant-card-2-content" type="inner" size="small">
            <Typography.Text type="secondary">
                This variant couldn&apos;t be generated. Regenerate to try again.
            </Typography.Text>
        </Card>
    </Card>
)

interface IPushlyAiSetup {
    domain: DomainDto
    flow: PushlyAiGenerationFlow
    onChange(options: PushlyAiGenerationOptions): void
}

/**
 * Choosing what to generate, laid out as the results will be: a card per variant, in the order the
 * results will show, each holding its settings in the well its result will fill. Controlled — the
 * options live with the flow, so Regenerate comes back to the same cards.
 */
export const PushlyAiSetup = ({ domain, flow, onChange }: IPushlyAiSetup) => {
    const { options } = flow
    const { slots, hasBrandVoice } = options
    const generating = flow.stage === 'generating'
    const total = slots.length
    const canAdd = !generating && total < PUSHLY_AI_NUM_VARIATIONS
    const canRemove = !generating && total > 1

    const setSlots = (next: PushlyAiVariantSlot[]) => onChange({ ...options, slots: next })

    return (
        <>
            <div className="ai-instructions-section">
                <div className="instructions-text">
                    {generating ? (
                        <>
                            <PushlyAi animate /> Generating {total} variant{total === 1 ? '' : 's'}. This can take up to
                            30 seconds.
                        </>
                    ) : (
                        <>
                            <PushlyAi /> Set up to {PUSHLY_AI_NUM_VARIATIONS} variants to generate from the provided
                            Landing URL: whether each uses your Brand Voice, and the tone it&apos;s written in.
                            {!hasBrandVoice && ' Set up a Brand Voice to write variants in your voice.'}
                        </>
                    )}
                </div>
                {!generating && flow.error && (
                    <Alert
                        type="warning"
                        showIcon
                        style={{ marginTop: 12 }}
                        title={<RemoteErrorMessageDisplay error={flow.error} />}
                    />
                )}
                {!generating && flow.empty && (
                    <Alert
                        type="info"
                        showIcon
                        style={{ marginTop: 12 }}
                        title="We were unable to generate any suggestions for the provided Landing URL. Try again, or adjust the variants."
                    />
                )}
            </div>

            {flow.brandVoiceLoading ? (
                <Skeleton active paragraph={{ rows: 6 }} title={false} />
            ) : (
                <div className="ai-variants-cards">
                    <Row gutter={[16, 16]}>
                        {slots.map((slot, i) => (
                            <Col key={i} xs={24} sm={12}>
                                <DraftVariantCard
                                    variant={i + 1}
                                    slot={slot}
                                    domain={domain}
                                    hasBrandVoice={hasBrandVoice}
                                    generating={generating}
                                    canRemove={canRemove}
                                    onChange={(next) => setSlots(slots.map((s, j) => (j === i ? next : s)))}
                                    onRemove={() => setSlots(slots.filter((_, j) => j !== i))}
                                />
                            </Col>
                        ))}
                        {canAdd && (
                            <Col xs={24} sm={12}>
                                <Button
                                    type="dashed"
                                    block
                                    className="add-variant-card"
                                    icon={<PlusOutlined />}
                                    onClick={() => setSlots([...slots, { brandVoice: hasBrandVoice, tone: null }])}
                                >
                                    Add variant
                                </Button>
                            </Col>
                        )}
                    </Row>
                </div>
            )}
        </>
    )
}
